/**
 * Tactile audio tick — synthesized via Web Audio API.
 * Renders identically on iPhone/Android/desktop (no vibrate hack required).
 *
 * Usage:
 *   import { playTick } from '../lib/tick';
 *   playTick('buy');  // higher/brighter (720Hz)
 *   playTick('sell'); // lower (520Hz)
 *
 * The AudioContext is lazily created inside a user-gesture handler
 * so browsers unlock it correctly. No permissions, no external files.
 */

let _ctx = null;

function getAudioCtx() {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try {
    _ctx = new Ctx();
  } catch (_) {
    return null;
  }
  return _ctx;
}

export function playTick(kind = 'buy') {
  const ctx = getAudioCtx();
  if (!ctx) return;
  // Some browsers start the context suspended. Resume on gesture.
  if (ctx.state === 'suspended') { ctx.resume().catch(() => {}); }

  try {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    // 'snap' is a soft, satisfying two-tone click for scroll-snap landings.
    // Kept subtle: shorter envelope, lower peak gain than buy/sell.
    if (kind === 'snap') {
      osc.type = 'triangle';
      osc.frequency.value = 880;
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.06, now + 0.004);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.exponentialRampToValueAtTime(1180, now + 0.03);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.05);
      return;
    }
    osc.type = 'sine';
    osc.frequency.value = kind === 'sell' ? 520 : 720;

    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.12, now + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.055);

    osc.connect(gain).connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.06);
  } catch (_) {
    // Silent fail — audio is enhancement, not required.
  }
}
